import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { profileHref } from "@/components/social/AuthorLink";
import { OtterAssistHelpButton } from "@/components/social/OtterAssistHelpButton";
import { HelperNoteEditor } from "@/components/social/HelperNote";
import { postHref, returnHref } from "@/lib/otterAssistRoutes";

// Otter Assist — the ONE canonical Need Detail (OAD-112 … OAD-114).
//
// Keyed on the ASSIST, so it serves a delivered recipient, a public-discovery helper with no
// delivery at all, and the sender/authorised manager. The payload is decided entirely by the
// backend: nothing here infers a relationship, and a "Why you matched" sentence is only ever
// rendered when the server actually returned one from that member's own delivery.
//
// The back control always reads "Return to Otter Assist" and restores a whitelisted internal origin.

const URGENCY_COPY = {
  immediate: "Immediate — today", asap: "ASAP — within 48 hours", soon: "Soon — within 7 days",
  within_days: "Within a set number of days", flexible: "Flexible — no firm deadline",
};
const STATUS_COPY = {
  active: "Open for help", help_received: "Help received", closed: "Closed", expired: "Expired",
  cancelled: "Cancelled",
};
const ROLE_COPY = {
  manager: "You posted this need",
  recipient: "Shared with you by Otter Assist",
  helper: "You found this post",
};

export default function OtterAssistNeed() {
  const { assistId } = useParams();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const from = searchParams.get("from");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["oa-need-detail", assistId],
    queryFn: async () => (await api.get(`/otter-assist/needs/${assistId}`)).data,
    enabled: !!assistId,
    retry: false,
  });
  const need = data?.need;
  const offer = need?.my_offer;

  return (
    <div className="mx-auto max-w-2xl px-4 pt-6 pb-10 sm:px-6" data-testid="otter-assist-need-page">
      <Button asChild size="sm" variant="ghost" className="mb-4 h-8 px-2 text-[12px]">
        <Link to={returnHref(from)} data-testid="otter-assist-need-back">
          <ArrowLeft className="mr-1 h-4 w-4" /><span>Return to Otter Assist</span>
        </Link>
      </Button>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : isError || !need ? (
        <p className="py-16 text-center text-sm text-muted-foreground"
          data-testid="otter-assist-need-missing">
          This need is no longer available to you.
        </p>
      ) : (
        <article className="rounded-xl border border-border bg-card p-5"
          data-testid={`otter-assist-need-${need.assist_id}`}>
          <div className="flex items-start gap-3">
            {need.poster?.headshot_url ? (
              <img src={need.poster.headshot_url} alt=""
                className="h-11 w-11 shrink-0 rounded-full object-cover" />
            ) : (
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-[640] text-muted-foreground">
                {(need.poster?.display_name || "M").slice(0, 1)}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                {profileHref(need.poster) ? (
                  <Link to={profileHref(need.poster)}
                    className="text-sm font-[640] text-foreground hover:underline"
                    data-testid="otter-assist-need-poster">
                    {need.poster?.display_name || "Member"}
                  </Link>
                ) : (
                  <span className="text-sm font-[640] text-foreground">
                    {need.poster?.display_name || "Member"}
                  </span>
                )}
                <span className="inline-flex items-center gap-1 rounded-full bg-teal-700 px-2 py-0.5 text-[9px] font-[700] uppercase tracking-[0.08em] text-white">
                  <Sparkles className="h-2.5 w-2.5" /> Otter Assist
                </span>
                <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-[600] uppercase tracking-wide text-muted-foreground"
                  data-testid="otter-assist-need-role">
                  {ROLE_COPY[need.viewer_role] || "Otter Assist"}
                </span>
              </div>

              <div className="mt-1 text-[11px] text-muted-foreground">
                <span data-testid="otter-assist-need-status">
                  {STATUS_COPY[need.assist_status] || need.assist_status}
                </span>
                {" · "}
                <span data-testid="otter-assist-need-urgency">
                  {URGENCY_COPY[need.urgency] || need.urgency}
                </span>
                {need.deadline_at
                  ? ` · Deadline ${new Date(need.deadline_at).toLocaleDateString()}`
                  : ""}
              </div>
            </div>
          </div>

          <p className="mt-4 whitespace-pre-line text-sm text-foreground/90"
            data-testid="otter-assist-need-summary">{need.summary}</p>

          {/* Only ever rendered from this member's OWN delivery — never invented for a
              public-discovery helper, who has no match record at all. */}
          {need.why_you_matched ? (
            <div className="mt-4 rounded-md border border-yellow-300/70 bg-yellow-50 px-3 py-2.5"
              data-testid="otter-assist-need-why">
              <div className="text-[10px] font-[700] uppercase tracking-wider text-teal-800">
                Why you matched
              </div>
              <p className="mt-0.5 text-[12px] text-foreground/90">{need.why_you_matched}</p>
            </div>
          ) : null}

          {need.viewer_role === "manager" ? (
            <p className="mt-4 text-[12px] text-muted-foreground"
              data-testid="otter-assist-need-responder-count">
              {need.responder_count === 1
                ? "1 person has offered to help."
                : `${need.responder_count || 0} people have offered to help.`}
            </p>
          ) : null}

          {/* The helper's own message — the SAME canonical field Assistance Offered edits. */}
          {offer ? (
            <HelperNoteEditor actionId={offer.action_id} note={offer.helper_note}
              withdrawn={offer.offer_state === "withdrawn"} testIdSuffix="need-detail"
              onSaved={() => qc.invalidateQueries({ queryKey: ["oa-need-detail", assistId] })} />
          ) : null}

          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
            {need.post_id && (
              <Button asChild size="sm" variant="outline" className="h-8 text-[11px]">
                <Link to={postHref(need.post_id, from)} data-testid="otter-assist-need-view-post">
                  <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> View Post
                </Link>
              </Button>
            )}
            {need.viewer_role !== "manager" && need.assist_status === "active" && (
              <OtterAssistHelpButton assistId={need.assist_id} testIdSuffix="need-detail"
                size="sm" />
            )}
          </div>
        </article>
      )}
    </div>
  );
}
